import { Tournament } from '@/src/domain/models';
import { fontSize, fontWeight, letterSpacing } from '@/src/core/utils/typography';
import { useTranslation } from '@/src/i18n';
import { FontAwesome5, Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

export interface TournamentCardProps {
  tournament: Tournament;
  onPress: () => void;
  onMenuPress?: (event: any) => void;
  followerCount?: number;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    divider: string;
    badgeSetup: string;
    badgeProgress: string;
    badgeCompleted: string;
    shadow: string;
  };
}

export const TournamentCard = ({ tournament, onPress, onMenuPress, followerCount, colors }: TournamentCardProps) => {
  const { t } = useTranslation();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'setup':
        return t('tournaments.setup');
      case 'in_progress':
        return t('tournaments.inProgress');
      case 'completed':
        return t('tournaments.completed');
      default:
        return status;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'single_elimination':
        return t('tournaments.singleElimination');
      case 'double_elimination':
        return t('tournaments.doubleElimination');
      case 'round_robin':
        return t('tournaments.roundRobin');
      case 'swiss':
        return t('tournaments.swiss');
      default:
        return type;
    }
  };

  return (
    <TouchableOpacity
      style={[styles.item, { backgroundColor: colors.card, borderColor: colors.border, shadowColor: colors.shadow }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.itemHeader}>
        <Text style={[styles.itemName, { color: colors.text }]}>{tournament.name}</Text>
        <View style={styles.itemRight}>
          <View
            style={[
              styles.badge,
              {
                backgroundColor:
                  tournament.status === 'completed'
                    ? colors.badgeCompleted
                    : tournament.status === 'in_progress'
                      ? colors.badgeProgress
                      : colors.badgeSetup,
              },
            ]}
          >
            <Text style={[styles.badgeText, { color: colors.text }]}>{getStatusLabel(tournament.status)}</Text>
          </View>
          {onMenuPress && (
            <TouchableOpacity style={styles.menuBtn} onPress={onMenuPress}>
              <Ionicons name="ellipsis-vertical" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>
      {tournament.description && (
        <Text style={[styles.itemDesc, { color: colors.textSecondary }]} numberOfLines={2} ellipsizeMode="tail">{tournament.description}</Text>
      )}
      <View style={[styles.itemInfo, { borderTopColor: colors.divider }]}>
        <View style={styles.infoRow}>
          {tournament.tournamentType === 'single_elimination' && (
            <>
              <FontAwesome5 name="trophy" size={12} color={colors.textTertiary} />
              <Text style={[styles.infoText, { color: colors.textTertiary }]}>{getTypeLabel(tournament.tournamentType)}</Text>
            </>
          )}
          {tournament.tournamentType === 'double_elimination' && (
            <>
              <MaterialCommunityIcons name="tournament" size={14} color={colors.textTertiary} />
              <Text style={[styles.infoText, { color: colors.textTertiary }]}>{getTypeLabel(tournament.tournamentType)}</Text>
            </>
          )}
          {tournament.tournamentType === 'round_robin' && (
            <>
              <MaterialCommunityIcons name="sync-circle" size={14} color={colors.textTertiary} />
              <Text style={[styles.infoText, { color: colors.textTertiary }]}>{getTypeLabel(tournament.tournamentType)}</Text>
            </>
          )}
          {tournament.tournamentType === 'swiss' && (
            <>
              <MaterialCommunityIcons name="grid" size={14} color={colors.textTertiary} />
              <Text style={[styles.infoText, { color: colors.textTertiary }]}>{getTypeLabel(tournament.tournamentType)}</Text>
            </>
          )}
        </View>
        <View style={styles.infoRow}>
          <Ionicons name="calendar-outline" size={12} color={colors.textTertiary} />
          <Text style={[styles.infoText, { color: colors.textTertiary }]}>
            {new Date(tournament.createdAt).toLocaleDateString()}
          </Text>
        </View>
        {followerCount !== undefined && (
          <View style={styles.infoRow}>
            <Ionicons name="heart" size={12} color={colors.textTertiary} />
            <Text style={[styles.infoText, { color: colors.textTertiary }]}>
              {followerCount}
            </Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  item: {
    marginHorizontal: 15,
    marginVertical: 8,
    padding: 15,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 3,
  },
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  itemName: {
    fontSize: fontSize.xl,
    fontWeight: fontWeight.bold,
    letterSpacing: letterSpacing.tight,
    flex: 1,
  },
  itemRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  badge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  badgeText: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.wide,
  },
  menuBtn: {
    padding: 4,
    paddingHorizontal: 8,
  },
  itemDesc: {
    fontSize: fontSize.base,
    lineHeight: 20,
    marginBottom: 8,
  },
  itemInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  infoText: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
  },
});
